import React from 'react';
import { styled } from 'styled-components';
import { Link } from 'react-router-dom';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useFindOne, useUser } from '/imports/hooks';
import { Icon } from '/imports/ui/Icon';
import { Accounts, Roles } from '/imports/api/accounts';

const ManagePageStyled = styled(Page)`
  .onboarding-banner {
    height: 4rem;
    background: ${css.red6};
    color: ${css.red7};
    font-weight: bold;
    text-decoration: underline;
    display: flex;
    gap: 1.5rem;
    justify-content: center;
    align-items: center;
    margin: 0 -1rem;
    span {
      cursor: pointer;
    }
  }
  .logout {
    text-align: center;
    margin: 1rem 0;
    span {
      cursor: pointer;
    }
  }
`;

/** Management landing page listing settings sections gated by user role. */
export const ManagePage = () => {
  const user = useUser();
  const userId = user?._id;
  const orgId = user?.orgId;
  const account = useFindOne(() => Accounts.find({orgId, userId}), [`${orgId}_${userId}`]);
  return (
    <ManagePageStyled>
      <PageHeading
        left={<h1>{strings.Manage()}</h1>}
      />
      <PageBody>
        <div className="items">
          <ManageItem title={strings.Profile()} subtitle={strings.NameEmailPassword()} icon="accountCircle" to="/profile" />
          {/* <ManageItem title={strings.Integrations()} subtitle={strings.ThirdPartyConnections()} icon="hub" to="/integrations" /> */}
          {
            user?.isSuperUser || account?.roles?.includes(Roles.admin) ?
              <ManageItem title={strings.Accounts()} subtitle={strings.AddOrModifyAccountsInYourOrg()} icon="idCard" to="/accounts" /> : null
          }
          {
            user?.isSuperUser ?
              <>
                <ManageItem title={strings.Users()} subtitle="" icon="person" to="/users" />
                <ManageItem title={strings.Organizations()} subtitle="" icon="group" to="/orgs" />
                <ManageItem title={strings.RolesAndAccess()} subtitle={strings.RolesAndAccessSubtitle()} icon="manageAccounts" to="/roles" />
                
                <ManageItem title={strings.Owners()} subtitle={strings.OwnersSubtitle()} icon="group" to="/project-management/owners" />
                <ManageItem title={strings.Epcs()} subtitle={strings.EpcsSubtitle()} icon="construction" to="/project-management/epcs" />
                <ManageItem title={strings.Utilities()} subtitle={strings.UtilitiesSubtitle()} icon="electricMeter" to="/project-management/utilities" />
                <ManageItem title={strings.Settings()} subtitle="" icon="settings" to="/settings" />
                <ManageItem title={strings.PrimeRates()} subtitle="" icon="percent" to="/prime-rates" />
                <ManageItem title={strings.Environment()} subtitle="" icon="dns" to="/environment" />
                <ManageItem title={strings.TestResults()} subtitle={strings.TestResultsSubtitle()} icon="circleCheck" to="/test-results" />
                <ManageItem title={strings.DevTools()} subtitle="" icon="construction" to="/devtools" />
              </>
              :
              null
          }
        </div>
        <div className="logout">
          <Link to="/logout">{strings.Logout()}</Link>
        </div>
      </PageBody>
    </ManagePageStyled>
  );
};

const ManageItemStyled = styled(Link)`
  display: flex;
  width: calc(100% + 2rem);
  margin: 0 -1rem;
  border-top: 1px solid ${css.grey6};
  padding: 0.75rem 0;
  &:last-child {
    border-bottom: 1px solid ${css.grey6};
  }
  .item-icon {
    width: 4rem;
    justify-content: center;
    display: inline-flex;
    align-items: center;
    svg {
      width: 1.75rem;
      height: 1.75rem;
      fill: ${css.primaryColor};
    }
  }
  .details {
    flex: 1;
    .title {
      font-weight: bold;
    }
  }
`;

interface ManageItemProps {
  title: string;
  subtitle: string;
  icon: string;
  to: string;
}

/** Single linked row in the manage list with icon, title, and subtitle. */
const ManageItem = (props: ManageItemProps) => (
  <ManageItemStyled to={props.to}>
    <div className="item-icon"><Icon icon={props.icon as any} /></div>
    <div className="details">
      <div className="title">{props.title}</div>
      <div>{props.subtitle}</div>
    </div>
    <div className="item-icon"><Icon icon="chevronRight" /></div>
  </ManageItemStyled>
);